// @ts-ignore
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { Component } from 'react';
import styled, { css } from 'styled-components';

import axios, { CancelTokenSource } from 'axios';
import SearchAPI from '../api/search.api';
import LoadingSpinner from './common/LoadingSpinner';
import PriceIndicator from './PriceIndicator';
import { withTranslation } from 'react-i18next';

const Container = styled.div<{ isClosed: boolean }>`
    display: block;
    width: 100%;
    border-radius: inherit;
    background-color: #fff;
    padding: 16px;
    z-index: 999;
    box-shadow: 0px 5px 16px rgba(126, 126, 126, 0.15);

    .header {
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        font-size: 14px;
        color: #9b9b9b;

        &.header-margin {
            margin-top: 16px;
        }

        &::after {
            content: '';
            display: block;
            width: 100%;
            height: 1px;
            margin: 16px 0px 8px 0px;
            background-color: #eeeeee;
        }
    }

    ${(props: { isClosed: boolean }) =>
        props.isClosed &&
        css`
            height: 0px;
            padding: 0px;
        `}
`;

const RestaurantResultItem = styled.div<{ isActive: boolean; index: number }>`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: top;
    left: -16px;
    padding: 8px 16px;
    width: calc(100% + 32px);
    opacity: 0;
    animation: fadeInResultItem 300ms ease-in
        ${(props: { index: number; isActive: boolean }) => props.index * 100}ms
        forwards;

    @keyframes fadeInResultItem {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    .info {
        > div:first-child {
            color: #333;
            font-size: 12px;
            margin-bottom: 8px;
        }
        > div:last-child {
            display: flex;
            align-items: center;
            color: #9b9b9b;
            font-size: 10px;
            > span {
                margin-right: 4px;
                &:nth-child(2) {
                    width: 4px;
                    height: 4px;
                    border-radius: 50%;
                    background-color: #eaeaea;
                }
                &:nth-child(3) {
                    height: 11px;
                    overflow: hidden;
                }
            }
        }
    }
    .price {
        font-size: 12px;
    }

    ${(props: { index: number; isActive: boolean }) =>
        props.isActive &&
        css`
            background-color: #f3f3f3;
        `}

    &:hover {
        cursor: pointer;
        background-color: #f3f3f3;
    }
`;

const NoResults = styled.div`
    font-size: 12px;
    color: #ccc;
`;

enum SearchResultType {
    Restaurant = 'restaurant',
    Category = 'category',
}

type Restaurant = {
    id: number;
    name: string;
    city: string;
    categories: string;
    priceCategoryId: number;
    listIndex: number;
    type: SearchResultType.Restaurant;
    slug: string;
};
type Category = {
    id: number;
    name: string;
    listIndex: number;
    type: SearchResultType.Category;
};

type SearchResults = {
    restaurants: Restaurant[];
};

const searchApi = new SearchAPI();
const searchRestaurantsAndCategories = (
    searchString: string,
    searchCancelToken: CancelTokenSource,
): Promise<SearchResults | null> =>
    new Promise((resolve) => {
        if (searchString.trim().length === 0) {
            // Skip fetching data if search string is empty
            resolve(null);
            return;
        }

        // If search string is valid, we search
        searchApi
            .searchRestaurantsAndCategories(
                searchString.trim(),
                searchCancelToken,
            )
            .then((res) => {
                let listIndexCounter = 0;
                const restaurants: Restaurant[] = res.data.restaurants.map(
                    (x) => ({
                        ...x,
                        listIndex: listIndexCounter++,
                        type: SearchResultType.Restaurant,
                    }),
                );

                resolve({
                    restaurants,
                });
            })
            .catch(() => {
                resolve(null);
            });
    });

const searchDebounced = AwesomeDebouncePromise(
    searchRestaurantsAndCategories,
    500,
);

type Props = {
    searchString: string;
    keyArrowPressed: {
        timestamp: number;
        direction: 'down' | 'up';
    } | null;
    returnFocus: () => void;
    onClick: (selectedItem: Category | Restaurant | null) => void;
    t: any;
};
type State = {
    isLoading: boolean;
    searchResults: SearchResults | null;
    activeItemIndex: number | null;
};

class SearchResultsDrawer extends Component<Props, State> {
    static defaultProps = {
        returnFocus: () => {},
    };

    searchCancelToken?: CancelTokenSource;

    constructor(props: Props) {
        super(props);

        this.state = {
            isLoading: false,
            searchResults: null,
            activeItemIndex: null,
        };
    }

    componentDidMount() {
        this.onKeyEnter = this.onKeyEnter.bind(this);
        document.addEventListener('keydown', this.onKeyEnter);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.onKeyEnter);

        if (this.searchCancelToken) {
            this.searchCancelToken.cancel();
        }
    }

    componentDidUpdate(prevProps: Props) {
        const hasSearchStringChanged =
            prevProps.searchString !== this.props.searchString;

        if (hasSearchStringChanged) {
            // Debounce search for a few ms before fetching data
            this.setState(
                {
                    isLoading: true,
                },
                () => {
                    this.searchCancelToken = axios.CancelToken.source();
                    searchDebounced(
                        this.props.searchString,
                        this.searchCancelToken,
                    ).then((result: any) => {
                        const isCancelled =
                            this.searchCancelToken!.token.reason;
                        if (!isCancelled) {
                            this.setState({
                                searchResults: result,
                                isLoading: false,
                                activeItemIndex: null,
                            });

                            if (result === null) {
                                // Give focus back to parent input if no search results
                                this.props.returnFocus();
                            }
                        }
                    });
                },
            );
        }

        if (
            this.props.keyArrowPressed &&
            prevProps.keyArrowPressed !== this.props.keyArrowPressed
        ) {
            if (this.state.searchResults) {
                if (
                    this.state.activeItemIndex === null &&
                    this.props.keyArrowPressed.direction === 'down'
                ) {
                    // If pressing down for the first time
                    this.setState({
                        activeItemIndex: 0,
                    });
                } else if (this.state.activeItemIndex !== null) {
                    // Calculate weather highlight goes up or down
                    let delta = 0;
                    delta +=
                        this.props.keyArrowPressed.direction === 'up' ? -1 : 1;

                    let newActiveStateIndex =
                        this.state.activeItemIndex + delta;

                    // Make sure we don't positively go out of bounds
                    const totalItemsCount =
                        this.state.searchResults.restaurants.length;
                    newActiveStateIndex =
                        newActiveStateIndex + 1 >= totalItemsCount
                            ? totalItemsCount - 1
                            : newActiveStateIndex;

                    this.setState(
                        {
                            activeItemIndex: newActiveStateIndex,
                        },
                        () => {
                            // Give focus back to parent if activeItemIndex goes negatively out of bounds
                            if (
                                this.state.activeItemIndex &&
                                this.state.activeItemIndex < 0
                            ) {
                                this.props.returnFocus();
                                this.setState({
                                    activeItemIndex: null,
                                });
                            }
                        },
                    );
                }
            }
        }
    }

    onKeyEnter(e: KeyboardEvent) {
        if (e.key === 'Enter') {
            // Key event enter
            if (
                this.state.searchResults &&
                this.state.activeItemIndex !== null
            ) {
                const flatList = [...this.state.searchResults.restaurants];
                // Pass active item that the user selected with arrow to the parent
                const activeItem: Category | Restaurant | undefined =
                    flatList.find(
                        (x) => x.listIndex === this.state.activeItemIndex,
                    );
                this.props.onClick(activeItem ? activeItem : null);
            } else {
                this.props.onClick(null);
            }
        }
    }

    render() {
        const { t } = this.props;

        return (
            <Container
                isClosed={!this.state.searchResults && !this.state.isLoading}
            >
                {!this.state.searchResults && this.state.isLoading && (
                    <div style={{ fontSize: '3px', padding: '8px' }}>
                        <LoadingSpinner />
                    </div>
                )}
                {this.state.searchResults && (
                    <div>
                        {this.state.searchResults &&
                            this.state.searchResults.restaurants.map((x, i) => (
                                <RestaurantResultItem
                                    key={`rest-${x.id}`}
                                    index={i}
                                    isActive={
                                        x.listIndex ===
                                        this.state.activeItemIndex
                                    }
                                    onClick={() => this.props.onClick(x)}
                                >
                                    <div className="info">
                                        <div>{x.name}</div>
                                        <div>
                                            <span>{x.city}</span>
                                            <span />
                                            <span>{x.categories}</span>
                                        </div>
                                    </div>
                                    <div className="price">
                                        <PriceIndicator
                                            priceCategoryId={x.priceCategoryId}
                                        />
                                    </div>
                                </RestaurantResultItem>
                            ))}
                        {this.state.searchResults &&
                            this.state.searchResults.restaurants.length ===
                                0 && (
                                <NoResults>
                                    {t(
                                        'components.searchResultDrawer.noResults',
                                    )}
                                </NoResults>
                            )}
                    </div>
                )}
            </Container>
        );
    }
}

export default withTranslation()(SearchResultsDrawer);
